import { useEffect, useState } from 'react';
import { Modal, ModalContent } from '@heroui/react';
import {
  ShoppingCart,
  Check,
  Shield,
  Lock,
  Wallet,
  CreditCard,
  Loader2,
  X,
  CalendarClock,
  Layers,
  Tag,
} from 'lucide-react';
import { formatCurrency } from '../../../../utils/currency';
import { CustomerPackage } from '../../../../utils/type';

export type OfferPaymentMethod = 'online' | 'cash';

interface AcceptOfferCheckoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pkg: CustomerPackage | null;
  isLoading?: boolean;
  onConfirm: (paymentMethod: OfferPaymentMethod) => void;
}

/** Full colors for color-mix / inline styles (dashboard tokens are HSL components). */
const C = {
  primary: 'hsl(var(--primary))',
  background: 'hsl(var(--background))',
  success: 'hsl(var(--success))',
  foreground: 'hsl(var(--foreground))',
  mutedFg: 'hsl(var(--muted-foreground))',
  border: 'hsl(var(--border))',
  card: 'hsl(var(--card))',
  primaryFg: 'hsl(var(--primary-foreground))',
};

/**
 * Phase 3.1 — "Session 1" / "Sessions 1, 2" label for an add-on, built from
 * its sessionNumbers snapshot. Legacy packages purchased before per-session
 * assignment existed have no sessionNumbers on their formatted addons (they
 * applied to every session at a flat price) — that case renders nothing,
 * matching the pre-3.1 display exactly.
 */
const sessionLabel = (sessionNumbers?: number[]): string | null => {
  if (!sessionNumbers || sessionNumbers.length === 0) return null;
  return `Appointment${sessionNumbers.length > 1 ? 's' : ''} ${sessionNumbers.join(', ')}`;
};

/**
 * Checkout modal shown when a customer accepts a provider-initiated package
 * offer. Visually ports the membership CheckoutModal's layout/tokens.
 * Online payment is not implemented yet — disabled, "Cash on Hand" is the
 * only usable (and default-selected) method.
 */
const AcceptOfferCheckoutModal = ({
  open,
  onOpenChange,
  pkg,
  isLoading = false,
  onConfirm,
}: AcceptOfferCheckoutModalProps) => {
  const [paymentMethod, setPaymentMethod] =
    useState<OfferPaymentMethod>('cash');

  // Reset to the only working option whenever the modal is (re)opened for a package.
  useEffect(() => {
    if (open) setPaymentMethod('cash');
  }, [open, pkg?.customerPackageId]);

  if (!pkg) return null;

  const addonSubtotal = pkg.addonSubtotal || 0;
  const packagePrice = pkg.tier?.price || 0;
  const total = pkg.totalPrice ?? pkg.total ?? packagePrice + addonSubtotal;
  const selectedAddons = pkg.additionalServices || [];

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      size="4xl"
      scrollBehavior="inside"
      hideCloseButton
      isDismissable={!isLoading}
      backdrop="opaque"
      classNames={{
        wrapper:
          'fixed inset-0 items-center justify-center !overflow-hidden p-2 sm:p-3',
        backdrop: 'bg-black/80',
        base: 'max-w-[820px] w-[calc(100vw-1rem)] max-h-[min(92vh,780px)] !my-0 bg-transparent shadow-none border-0 overflow-hidden',
        body: 'p-0 overflow-hidden',
      }}
    >
      <ModalContent className="p-0 gap-0 bg-transparent shadow-none border-0 max-h-[min(92vh,780px)] overflow-hidden">
        {(onClose) => (
          <div
            className="relative rounded-2xl shadow-2xl max-h-[min(92vh,780px)] overflow-y-auto overscroll-contain"
            style={{ background: C.card, color: C.foreground }}
          >
            <button
              type="button"
              aria-label="Close"
              disabled={isLoading}
              onClick={() => {
                onClose();
                onOpenChange(false);
              }}
              className="absolute right-3 top-3 z-20 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none"
              style={{ color: C.mutedFg }}
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative px-3 sm:px-5 lg:px-6 pt-4 pb-4">
              {/* Header */}
              <div className="flex items-start gap-2.5 mb-3 pr-7">
                <div
                  className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center"
                  style={{
                    background: `color-mix(in oklab, ${C.primary} 10%, transparent)`,
                  }}
                >
                  <ShoppingCart
                    className="h-4 w-4"
                    style={{ color: C.primary }}
                  />
                </div>
                <div>
                  <h2
                    className="text-base sm:text-lg font-bold tracking-tight leading-tight"
                    style={{ color: C.foreground }}
                  >
                    Checkout
                  </h2>
                  <p
                    className="mt-0.5 text-[11px] sm:text-xs"
                    style={{ color: C.mutedFg }}
                  >
                    Review this package offer before you accept it
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-3 lg:gap-4">
                {/* LEFT: Package overview + payment method */}
                <div className="flex flex-col gap-2.5 sm:gap-3">
                  <div
                    className="rounded-xl p-3 sm:p-3.5 border relative overflow-hidden"
                    style={{
                      borderColor: C.border,
                      background: `color-mix(in oklab, ${C.primary} 6%, ${C.background})`,
                    }}
                  >
                    <div className="flex flex-row items-start gap-3">
                      <div
                        className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-xl flex items-center justify-center"
                        style={{ background: 'var(--gradient-primary)' }}
                      >
                        <Layers className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span
                          className="inline-block text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full"
                          style={{
                            background: 'var(--gradient-primary)',
                            color: C.primaryFg,
                          }}
                        >
                          Package Offer
                        </span>
                        <h3
                          className="mt-1 text-lg sm:text-xl font-bold leading-tight"
                          style={{ color: C.foreground }}
                        >
                          {pkg.tier?.name || 'Package'}
                        </h3>
                        <p
                          className="mt-0.5 text-xs"
                          style={{ color: C.mutedFg }}
                        >
                          {pkg.sessionsTotal} appointment
                          {pkg.sessionsTotal === 1 ? '' : 's'}
                          {pkg.tier?.validityDays
                            ? ` · Valid ${pkg.tier.validityDays} days`
                            : ''}
                        </p>
                        <div className="mt-0.5 flex items-baseline gap-1">
                          <span
                            className="text-xl sm:text-2xl font-bold"
                            style={{ color: C.foreground }}
                          >
                            {formatCurrency(packagePrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* What's Included */}
                  <div>
                    <h4
                      className="text-xs sm:text-sm font-bold mb-1.5"
                      style={{ color: C.foreground }}
                    >
                      What&apos;s Included
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      <li
                        className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5"
                        style={{
                          borderColor: C.border,
                          background: C.background,
                        }}
                      >
                        <span
                          className="h-4 w-4 shrink-0 rounded-full flex items-center justify-center"
                          style={{
                            background: `color-mix(in oklab, ${C.success} 15%, transparent)`,
                          }}
                        >
                          <Check
                            className="h-2.5 w-2.5"
                            style={{ color: C.success }}
                            strokeWidth={3}
                          />
                        </span>
                        <CalendarClock
                          className="h-3.5 w-3.5 shrink-0"
                          style={{ color: C.mutedFg }}
                        />
                        <span
                          className="text-[11px] sm:text-xs truncate"
                          style={{ color: C.foreground }}
                        >
                          {pkg.sessionsTotal} appointment
                          {pkg.sessionsTotal === 1 ? '' : 's'}
                        </span>
                      </li>
                      {selectedAddons.map((addon) => {
                        const sessions = sessionLabel(addon.sessionNumbers);
                        return (
                          <li
                            key={addon.id}
                            className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5"
                            style={{
                              borderColor: C.border,
                              background: C.background,
                            }}
                          >
                            <span
                              className="h-4 w-4 shrink-0 rounded-full flex items-center justify-center"
                              style={{
                                background: `color-mix(in oklab, ${C.success} 15%, transparent)`,
                              }}
                            >
                              <Check
                                className="h-2.5 w-2.5"
                                style={{ color: C.success }}
                                strokeWidth={3}
                              />
                            </span>
                            <Tag
                              className="h-3.5 w-3.5 shrink-0"
                              style={{ color: C.mutedFg }}
                            />
                            <span
                              className="text-[11px] sm:text-xs truncate min-w-0"
                              style={{ color: C.foreground }}
                            >
                              {addon.serviceItem}
                              {sessions && (
                                <span
                                  className="block text-[10px] leading-tight"
                                  style={{ color: C.mutedFg }}
                                >
                                  {sessions}
                                </span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <h4
                      className="text-xs sm:text-sm font-bold mb-1.5"
                      style={{ color: C.foreground }}
                    >
                      Payment Method
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {/* Online — temporarily disabled, not implemented yet */}
                      <button
                        type="button"
                        disabled
                        title="Online payment is coming soon"
                        className="relative flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left opacity-50 cursor-not-allowed"
                        style={{
                          borderColor: C.border,
                          background: C.background,
                        }}
                      >
                        <CreditCard
                          className="h-4 w-4 shrink-0"
                          style={{ color: C.mutedFg }}
                        />
                        <div className="min-w-0">
                          <p
                            className="text-xs font-semibold truncate"
                            style={{ color: C.foreground }}
                          >
                            Pay Online
                          </p>
                          <p
                            className="text-[10px] truncate"
                            style={{ color: C.mutedFg }}
                          >
                            Coming soon
                          </p>
                        </div>
                      </button>

                      {/* Cash on Hand — default + only usable option */}
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('cash')}
                        className="relative flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors"
                        style={{
                          borderColor:
                            paymentMethod === 'cash' ? C.primary : C.border,
                          background:
                            paymentMethod === 'cash'
                              ? `color-mix(in oklab, ${C.primary} 8%, ${C.background})`
                              : C.background,
                        }}
                      >
                        <Wallet
                          className="h-4 w-4 shrink-0"
                          style={{
                            color:
                              paymentMethod === 'cash' ? C.primary : C.mutedFg,
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-xs font-semibold truncate"
                            style={{ color: C.foreground }}
                          >
                            Cash on Hand
                          </p>
                          <p
                            className="text-[10px] truncate"
                            style={{ color: C.mutedFg }}
                          >
                            Pay the provider directly
                          </p>
                        </div>
                        {paymentMethod === 'cash' && (
                          <span
                            className="h-4 w-4 shrink-0 rounded-full flex items-center justify-center"
                            style={{ background: C.primary }}
                          >
                            <Check
                              className="h-2.5 w-2.5 text-white"
                              strokeWidth={3}
                            />
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Order summary */}
                <div
                  className="rounded-xl border p-3 sm:p-3.5 flex flex-col gap-2.5 h-fit"
                  style={{ borderColor: C.border, background: C.background }}
                >
                  <div>
                    <h4
                      className="text-sm font-bold"
                      style={{ color: C.foreground }}
                    >
                      Order Summary
                    </h4>
                    <div className="mt-2 flex items-center justify-between text-xs sm:text-sm">
                      <span style={{ color: C.foreground }}>
                        {pkg.tier?.name || 'Package'}
                      </span>
                      <span
                        className="font-semibold"
                        style={{ color: C.foreground }}
                      >
                        {formatCurrency(packagePrice)}
                      </span>
                    </div>

                    {/* Add-ons — itemized per addon with its session numbers
                        (Phase 3.1), so an addon priced for only 1 of 2
                        sessions is visibly distinct from one covering every
                        session, rather than a single lumped total. */}
                    {selectedAddons.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p
                          className="text-[10px] font-semibold uppercase tracking-wide"
                          style={{ color: C.mutedFg }}
                        >
                          Add-on Services
                        </p>
                        {selectedAddons.map((addon) => {
                          const sessions = addon.sessionNumbers?.length
                            ? addon.sessionNumbers
                            : [];
                          const sessionCount = sessions.length || 1;
                          const label = sessionLabel(addon.sessionNumbers);
                          return (
                            <div
                              key={addon.id}
                              className="flex items-start justify-between gap-2 text-xs sm:text-sm"
                            >
                              <span
                                className="truncate min-w-0"
                                style={{ color: C.foreground }}
                              >
                                {addon.serviceItem}
                                {label && (
                                  <span
                                    className="block text-[10px]"
                                    style={{ color: C.mutedFg }}
                                  >
                                    {label}
                                  </span>
                                )}
                              </span>
                              <span
                                className="font-semibold shrink-0"
                                style={{ color: C.foreground }}
                              >
                                {formatCurrency(
                                  (addon.price || 0) * sessionCount
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {addonSubtotal > 0 && (
                      <div
                        className="flex items-center justify-between text-xs sm:text-sm mt-1.5 pt-1.5 border-t border-dashed"
                        style={{ borderColor: C.border }}
                      >
                        <span style={{ color: C.foreground }}>
                          Add-ons Subtotal
                        </span>
                        <span
                          className="font-semibold"
                          style={{ color: C.foreground }}
                        >
                          {formatCurrency(addonSubtotal)}
                        </span>
                      </div>
                    )}
                    <div
                      className="my-2 border-t"
                      style={{ borderColor: C.border }}
                    />
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm font-bold"
                        style={{ color: C.primary }}
                      >
                        Total Due
                      </span>
                      <span
                        className="text-base sm:text-lg font-bold"
                        style={{ color: C.primary }}
                      >
                        {formatCurrency(total)}
                      </span>
                    </div>
                    <div
                      className="mt-1 flex items-center gap-1 text-[10px] sm:text-xs"
                      style={{ color: C.mutedFg }}
                    >
                      <Lock className="h-3 w-3" />
                      Paid directly to the provider (
                      {paymentMethod === 'cash' ? 'Cash on Hand' : 'Online'})
                    </div>
                  </div>

                  <div
                    className="rounded-lg p-2.5 flex items-start gap-2 border"
                    style={{
                      background: `color-mix(in oklab, ${C.primary} 6%, ${C.background})`,
                      borderColor: `color-mix(in oklab, ${C.primary} 20%, transparent)`,
                    }}
                  >
                    <Shield
                      className="h-4 w-4 shrink-0 mt-0.5"
                      style={{ color: C.primary }}
                    />
                    <div className="text-[11px] sm:text-xs leading-snug">
                      <div
                        className="font-bold"
                        style={{ color: C.foreground }}
                      >
                        Accepting this offer
                      </div>
                      <div style={{ color: C.mutedFg }}>
                        Activates the package so you can start booking
                        appointments right away.
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => onConfirm(paymentMethod)}
                    className="w-full h-10 sm:h-11 text-xs sm:text-sm font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-80 disabled:pointer-events-none transition-opacity shrink-0"
                    style={{
                      background: 'var(--gradient-popular)',
                      color: C.primaryFg,
                    }}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Lock className="h-3.5 w-3.5" />
                    )}
                    {isLoading ? 'Processing...' : 'Confirm & Accept Offer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default AcceptOfferCheckoutModal;
